/**
 * Findings sidebar — Redesign v3 (UI_SDD §R.4a, DEC-061 amendment).
 *
 * Replaces the old ring gauge + recharts donut with the design file's linear
 * treatments: a mono-number Risk card with a tri-color band, thin horizontal
 * severity bars, and category rows. Severity bars and category rows keep the
 * click-to-filter behavior (§4.3).
 *
 * DEC-189: Debt Index, Severity and By category only. Coverage and "Not examined" moved
 * to the "What TDIA checked" tab (CheckedTab), where they have room to be read.
 */
import type { ScanReportResponse, Severity } from '../../types/api'
import { CATEGORY_LABELS } from '../../types/api'
import { dollars } from '../../utils/format'
import { TonePill, type PillTone } from '../shared/Badge'

// Canonical severity ramp (DEC-068); must stay in sync with tailwind.config.ts.
// DEC-193: the bar takes the mark hue (the pill dot); the label takes readable text.
// High's label is `high-label`, bright orange on dark and ink on white, because
// bright orange text cannot be read on white.
const SEVERITY_CONFIG: Array<{ key: Severity; label: string; color: string; bar: string }> = [
  { key: 'critical', label: 'Critical', color: 'rgb(var(--critical))', bar: 'rgb(var(--mark-critical))' },
  { key: 'high', label: 'High', color: 'rgb(var(--high-label))', bar: 'rgb(var(--mark-high))' },
  { key: 'medium', label: 'Medium', color: 'rgb(var(--medium))', bar: 'rgb(var(--mark-medium))' },
  { key: 'low', label: 'Low', color: 'rgb(var(--low))', bar: 'rgb(var(--mark-low))' },
]

interface SummaryPanelProps {
  report: ScanReportResponse
  onCategoryClick?: (category: string | null) => void
  activeCategory?: string | null
  onSeverityClick?: (severity: Severity | null) => void
  activeSeverity?: Severity | null
}

/**
 * Debt Index is read against the repository's own baseline of 100, not against an
 * absolute scale (DEC-107). There is no fixed 0-100 ramp to colour, so the colour
 * encodes DIRECTION versus baseline. Amber here is a status, not a severity, which
 * is the only use DEC-068 permits for it.
 */
function indexDirection(index: number | null, isBaseline: boolean): {
  label: string
  token: string
  mark: string
  tone: PillTone
} {
  // Two tokens (DEC-132). `token` is the text ramp (the pill is ink since DEC-193). `mark` is
  // the vivid ramp, for the 30px+ numeral: that is LARGE text, so its gate is
  // 3:1 rather than 4.5:1, and on paper the text ramp made the headline figure
  // of the whole card the dullest thing on it. They are equal on dark.
  if (isBaseline || index == null)
    return { label: 'Baseline', token: 'accent-text', mark: 'mark-running', tone: 'running' }
  if (index >= 105)
    return { label: 'Worse than baseline', token: 'critical', mark: 'mark-critical', tone: 'critical' }
  if (index <= 95)
    return { label: 'Better than baseline', token: 'success', mark: 'mark-success', tone: 'success' }
  return { label: 'Flat vs baseline', token: 'warn', mark: 'mark-warn', tone: 'warn' }
}

function densityUnit(basis: 'kloc' | 'files'): string {
  return basis === 'kloc' ? 'per 1k lines' : 'per 1k files'
}

export function SummaryPanel({
  report,
  onCategoryClick,
  activeCategory,
  onSeverityClick,
  activeSeverity,
}: SummaryPanelProps) {
  const debt = report.debt_index ?? null
  const direction = indexDirection(debt?.debt_index ?? null, debt?.is_baseline ?? false)
  const bySeverity: Record<string, number> = report.summary?.findings_by_severity ?? {}
  const maxSev = Math.max(1, ...SEVERITY_CONFIG.map((s) => bySeverity[s.key] ?? 0))

  const categories = Object.entries<number>(report.summary?.findings_by_category ?? {})
    .sort((a, b) => b[1] - a[1])
  const maxCat = Math.max(1, ...categories.map(([, n]) => n))

  return (
    <div className="flex flex-col gap-[14px]">
      {/* ── Debt Index card (DEC-110: replaced the risk-score gauge) ── */}
      <div className="bg-panel border border-hairline rounded-lg shadow-card p-[18px]">
        <div className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted mb-[13px]">
          Debt index
        </div>
        {debt == null ? (
          <div className="text-[12px] text-body mb-[14px]">
            No findings recorded for this scan, so there is no debt index to report.
          </div>
        ) : (
          <>
            {/* Wraps instead of overflowing: in the narrow column the pill moves under the
                number rather than spilling out of the card (founder, 2026-10-12). */}
            <div className="flex flex-wrap items-center gap-x-[10px] gap-y-2 mb-[9px] min-w-0">
              <span
                className="font-medium tabular-nums text-[38px] leading-none tracking-[-1px]"
                style={{ color: `rgb(var(--${direction.mark}))` }}
              >
                {debt.debt_index == null ? '--' : Math.round(debt.debt_index)}
              </span>
              <span className="max-w-full">
                <TonePill tone={direction.tone}>{direction.label}</TonePill>
              </span>
            </div>
            {/*
              The density is rendered with the index, never instead of it: the index alone
              hides whether this repository started clean or filthy (SDD §34.3).
            */}
            <div className="flex items-baseline gap-[6px] mb-[11px]">
              <span className="font-medium tabular-nums text-[13px] text-body">
                {debt.debt_density.toFixed(1)}
              </span>
              <span className="text-[11px] text-faint">
                debt units {densityUnit(debt.denominator_basis)}
              </span>
            </div>
            {debt.comparability === 'degraded' && debt.comparability_reason && (
              <div className="text-[11px] leading-[1.5] text-warn mb-[11px]">
                {debt.comparability_reason}
              </div>
            )}
            <details className="mb-[14px]">
              <summary className="cursor-pointer text-[10px] uppercase tracking-[1px] text-muted">
                How this is calculated
              </summary>
              <p className="mt-[8px] text-[11px] leading-[1.6] text-body">
                {debt.disclosure}
              </p>
            </details>
          </>
        )}
        <div className="flex justify-between py-2 border-t border-fill-subtle">
          <span className="text-[12px] text-body">Total effort</span>
          <span className="font-medium tabular-nums text-[12px]">
            {Math.round(report.summary?.total_effort_hours ?? 0)} h
          </span>
        </div>
        <div className="flex justify-between py-2 border-t border-fill-subtle">
          <span className="text-[12px] text-body">Dollar cost</span>
          <span className="font-medium tabular-nums text-[12px] text-critical">
            {dollars(report.summary?.total_dollar_cost ?? 0)}
          </span>
        </div>
      </div>

      {/* ── Severity bars (clickable filter) ── */}
      <div className="bg-panel border border-hairline rounded-lg shadow-card p-[18px]">
        <div className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted mb-[15px]">
          Severity
        </div>
        <div className="flex flex-col gap-[11px]">
          {SEVERITY_CONFIG.map(({ key, label, color, bar }) => {
            const count = bySeverity[key] ?? 0
            const isActive = activeSeverity === key
            const dimmed = activeSeverity != null && !isActive
            return (
              <button
                key={key}
                onClick={() => onSeverityClick?.(isActive ? null : key)}
                className={`flex items-center gap-[10px] w-full text-left transition-opacity ${
                  dimmed ? 'text-faint' : ''
                }`}
                title={isActive ? 'Clear filter' : `Filter: ${label}`}
              >
                <span
                  className="font-medium text-[11px] w-[58px] flex-none"
                  style={{ color }}
                >
                  {label}
                </span>
                <span className="flex-1 h-[7px] bg-fill-subtle rounded overflow-hidden">
                  <span
                    className="block h-full rounded transition-[width] duration-300"
                    style={{ width: `${(count / maxSev) * 100}%`, background: bar }}
                  />
                </span>
                <span className="font-medium tabular-nums text-[11px] text-body w-[22px] text-right flex-none">
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Category rows (clickable filter) ── */}
      {categories.length > 0 && (
        <div className="bg-panel border border-hairline rounded-lg shadow-card p-[18px]">
          <div className="font-semibold text-[10px] leading-none tracking-[2px] uppercase text-muted mb-[15px]">
            By category
          </div>
          <div className="flex flex-col gap-[10px]">
            {categories.map(([cat, count]) => {
              const isActive = activeCategory === cat
              const dimmed = activeCategory != null && !isActive
              return (
                <button
                  key={cat}
                  onClick={() => onCategoryClick?.(isActive ? null : cat)}
                  className={`flex items-center gap-[10px] w-full text-left transition-opacity ${
                    dimmed ? 'text-faint' : ''
                  }`}
                  title={isActive ? 'Clear filter' : `Filter: ${CATEGORY_LABELS[cat] ?? cat}`}
                >
                  <span
                    className={`text-[11px] flex-1 truncate ${
                      isActive ? 'font-semibold text-accent-text' : 'font-medium text-body'
                    }`}
                  >
                    {CATEGORY_LABELS[cat] ?? cat}
                  </span>
                  <span className="w-[72px] h-[7px] bg-fill-subtle rounded overflow-hidden flex-none">
                    <span
                      className={`block h-full rounded ${isActive ? 'bg-accent' : 'bg-faint'}`}
                      style={{ width: `${(count / maxCat) * 100}%` }}
                    />
                  </span>
                  <span className="font-medium tabular-nums text-[11px] text-body w-[22px] text-right flex-none">
                    {count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
